import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Plus, Search, Edit, Trash2, XCircle, MapPin, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const ZONAS = ["Turén", "Acarigua", "El Playón", "Píritu", "Guanare"];

const formVacio = () => ({
  nombre: "", apellidos: "", cedula: "", telefono: "",
  zona: "", direccion: "", email: "", callmebot_apikey: "",
});

export default function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(formVacio());

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/clientes`);
      setClientes(data);
      setFiltered(data);
    } catch {
      toast.error("Error al cargar clientes");
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      clientes.filter(c =>
        `${c.nombre} ${c.apellidos || ""}`.toLowerCase().includes(q) ||
        (c.cedula || "").includes(q) ||
        c.telefono.includes(q) ||
        (c.zona || "").toLowerCase().includes(q)
      )
    );
  }, [search, clientes]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      if (editing) {
        await axios.put(`${API}/clientes/${editing.id}`, payload);
        toast.success("Cliente actualizado");
      } else {
        await axios.post(`${API}/clientes`, payload);
        toast.success("Cliente creado");
      }
      setOpen(false);
      reset();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const handleEdit = (c) => {
    setEditing(c);
    setForm({
      nombre: c.nombre || "", apellidos: c.apellidos || "", cedula: c.cedula || "",
      telefono: c.telefono || "", zona: c.zona || "", direccion: c.direccion || "",
      email: c.email || "", callmebot_apikey: c.callmebot_apikey || "",
    });
    setOpen(true);
  };

  const handleInactivar = async (id, nombre) => {
    if (!window.confirm(`¿Inactivar a ${nombre}?`)) return;
    try {
      await axios.put(`${API}/clientes/${id}/inactivar`);
      toast.success("Cliente inactivado");
      load();
    } catch { toast.error("Error al inactivar"); }
  };

  const handleDelete = async (id, nombre) => {
    if (!window.confirm(`⚠️ ¿ELIMINAR a ${nombre} y todas sus inscripciones? No se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/clientes/${id}`);
      toast.success("Cliente eliminado");
      load();
    } catch { toast.error("Error al eliminar"); }
  };

  const reset = () => { setForm(formVacio()); setEditing(null); };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Clientes</h1>
          <p className="text-sm text-slate-500">{clientes.filter(c => c.activo).length} activos de {clientes.length} total</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
          <DialogTrigger asChild>
            <Button className="bg-red-700 hover:bg-red-800 text-white">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar Cliente" : "Nuevo Cliente"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nombre *</Label>
                  <Input value={form.nombre} onChange={set("nombre")} required />
                </div>
                <div>
                  <Label>Apellidos *</Label>
                  <Input value={form.apellidos} onChange={set("apellidos")} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Cédula *</Label>
                  <Input placeholder="V-12345678" value={form.cedula} onChange={set("cedula")} required />
                </div>
                <div>
                  <Label>Teléfono * (con +58)</Label>
                  <Input placeholder="+584121234567" value={form.telefono} onChange={set("telefono")} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Zona</Label>
                  <select
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm mt-0.5"
                    value={form.zona} onChange={set("zona")}
                  >
                    <option value="">Selecciona zona</option>
                    {ZONAS.map(z => <option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={set("email")} />
                </div>
              </div>
              <div>
                <Label>Dirección (para delivery)</Label>
                <Input value={form.direccion} onChange={set("direccion")} />
              </div>
              <div>
                <Label>API Key CallMeBot (WhatsApp)</Label>
                <Input placeholder="Código que le envió CallMeBot al cliente" value={form.callmebot_apikey} onChange={set("callmebot_apikey")} />
                <p className="text-xs text-blue-600 mt-1">
                  El cliente agrega +34 684 77 00 05 en WhatsApp y envía: <em>"I allow callmebot to send me messages"</em>
                </p>
              </div>
              <Button type="submit" className="w-full bg-red-700 hover:bg-red-800">
                {editing ? "Actualizar" : "Crear"} Cliente
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Buscar por nombre, cédula, teléfono o zona..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Cédula</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Zona</TableHead>
                <TableHead>Dirección</TableHead>
                <TableHead>WhatsApp</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-slate-400">
                    No se encontraron clientes
                  </TableCell>
                </TableRow>
              ) : filtered.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">
                    {c.nombre} {c.apellidos || ""}
                  </TableCell>
                  <TableCell className="text-slate-500">{c.cedula || "-"}</TableCell>
                  <TableCell>{c.telefono}</TableCell>
                  <TableCell>
                    {c.zona
                      ? <span className="flex items-center gap-1 text-slate-600 text-xs"><MapPin className="w-3 h-3 text-red-500" />{c.zona}</span>
                      : <span className="text-slate-300">-</span>}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500 max-w-[160px] truncate">{c.direccion || "-"}</TableCell>
                  <TableCell>
                    {c.callmebot_apikey
                      ? <CheckCircle className="w-4 h-4 text-green-500" />
                      : <span className="text-xs text-slate-300">Sin configurar</span>}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.activo ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                      {c.activo ? "Activo" : "Inactivo"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(c)} className="text-blue-600 hover:bg-blue-50 h-8 w-8 p-0">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleInactivar(c.id, c.nombre)} className="text-orange-500 hover:bg-orange-50 h-8 w-8 p-0" title="Inactivar">
                        <XCircle className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id, c.nombre)} className="text-red-600 hover:bg-red-50 h-8 w-8 p-0" title="Eliminar">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
